import { IntradayChartPoint, PreparedTrade } from './intraday-chart.model';

export function isValidTradePrice(price: number): boolean {
	return Number.isFinite(price) && price > 0;
}

/** `2026-08-24T11:58:20+00:00` → the date/time parts, offset deliberately ignored. */
const SESSION_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;

/**
 * Reads an intraday timestamp as exchange wall-clock time.
 *
 * The endpoint stamps every point `+00:00`, but the values are already exchange-local: a
 * session running `11:58` → `15:10` lines up with the sparkline's 12:00-17:00 session window
 * only when read that way. Parsing them as UTC shifts the series hours past the close, where
 * `mapTradesToPoints` clamps it into a spike on the chart's right edge.
 *
 * Returns `NaN` for anything that isn't a timestamp, so the trade is dropped by `prepareTrades`.
 */
export function parseSessionTime(time: string): number {
	const parts = SESSION_TIME_PATTERN.exec(time);
	if (!parts) {
		return NaN;
	}

	const [, year, month, day, hour, minute, second] = parts.map(Number);
	return new Date(year, month - 1, day, hour, minute, second).getTime();
}

/**
 * Resolves each point's API timestamp to epoch milliseconds and drops what cannot be plotted:
 * unparsable times and zero / invalid prices. The result is sorted by time, which every
 * downstream builder relies on — the trend chart reads its first and last entries as the
 * bounds of its time domain.
 */
export function prepareTrades(points: readonly IntradayChartPoint[]): PreparedTrade[] {
	return points
		.map(point => ({ timeMs: parseSessionTime(point.time), price: point.price }))
		.filter(trade => isValidTradePrice(trade.price) && Number.isFinite(trade.timeMs))
		.sort((a, b) => a.timeMs - b.timeMs);
}
